import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";
import { OPERATIONS } from "../nav";
import type { SloObjective, SloResponse, SloStatus } from "../api/types";

const STATUS_META: Record<SloStatus, { label: string; cls: string }> = {
  "fast-burn": { label: "Fast burn",  cls: "crit" },
  "exhausted": { label: "Exhausted",  cls: "crit" },
  "slow-burn": { label: "Slow burn",  cls: "warn" },
  "ok":        { label: "Healthy",    cls: "ok" },
  "no-data":   { label: "No data",    cls: "muted" },
};

// Worst first: burning/exhausted, then lowest budget remaining, no-data last.
const RANK: Record<SloStatus, number> = { "fast-burn": 0, "exhausted": 1, "slow-burn": 2, "ok": 3, "no-data": 4 };

// Shown on hover (title) so the burn states aren't a mystery.
const STATUS_HELP: Record<SloStatus, string> = {
  "fast-burn": "Fast burn — ~14.4× the steady budget-spend rate; budget exhausts in days (pages)",
  "exhausted": "Exhausted — the 30d error budget is gone",
  "slow-burn": "Slow burn — ~6× the steady budget-spend rate; will exhaust this month",
  "ok":        "Healthy — error budget on track",
  "no-data":   "No data — no metrics available",
};

export function SloTab() {
  const meta = OPERATIONS.find((e) => e.id === "slo")!;
  const { data, loading, error } = useApi<SloResponse>("/command-center/slo/all");

  if (loading) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="loading"><div className="loading-spinner" /><p>Querying error budgets…</p></div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="empty">Could not load SLOs. Is command-center-api running?</div>
      </>
    );
  }
  if (data.status === "no-defs") {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="empty">
          No SLO definitions found. Add Sloth specs under <code>WaffyMonitoring/slo/definitions/</code>.
        </div>
      </>
    );
  }

  const slos = [...data.slos].sort((a, b) =>
    RANK[a.status] - RANK[b.status] ||
    (a.budget_remaining_pct ?? 999) - (b.budget_remaining_pct ?? 999) ||
    a.key.localeCompare(b.key));

  const burning = slos.filter((s) => s.status === "fast-burn" || s.status === "exhausted").length;
  const atRisk  = slos.filter((s) => s.status === "slow-burn").length;
  const healthy = slos.filter((s) => s.status === "ok").length;

  return (
    <>
      <ContentHeader title={meta.label} count={slos.length} description={meta.desc} />

      {data.status === "unavailable" && (
        <div className="empty" style={{ marginBottom: 18, padding: "14px 18px", textAlign: "start" }}>
          Showing defined SLOs only — live budgets need <code>GRAFANA_TOKEN</code> on the API to query Prometheus.
          {data.reason && <div style={{ marginTop: 4, fontSize: 11.5, color: "var(--text-muted)" }}>{data.reason}</div>}
        </div>
      )}

      <div className="cov-summary">
        <span className={`cov-pill ${burning ? "crit" : "ok"}`}><span className="dot" /><strong>{burning}</strong> burning</span>
        <span className={`cov-pill ${atRisk ? "warn" : "ok"}`}><span className="dot" /><strong>{atRisk}</strong> at risk</span>
        <span className="cov-pill ok"><span className="dot" /><strong>{healthy}</strong> healthy</span>
      </div>

      <p className="legend-note">
        Error budget = the allowed failure over 30d. Burn rate = how fast it's being spent vs the steady rate.{" "}
        <strong>Healthy</strong> = budget on track · <strong>Slow burn</strong> = ~6× (will exhaust this month) ·{" "}
        <strong>Fast burn</strong> = ~14.4× (exhausts in days — pages) · <strong>Exhausted</strong> = budget gone ·{" "}
        <strong>No data</strong> = no metrics.
      </p>

      <div className="grid">
        {slos.map((s) => <SloCard key={s.key} slo={s} />)}
      </div>
    </>
  );
}

function SloCard({ slo }: { slo: SloObjective }) {
  const meta = STATUS_META[slo.status];
  const rem = slo.budget_remaining_pct;
  const barPct = rem == null ? 0 : Math.max(0, Math.min(100, rem));

  return (
    <div className={`slo-card budget-${meta.cls}`}>
      <div className="card-head">
        <div>
          <div className="card-name">{slo.slo_group}</div>
          <div className="slo-sub">{slo.name}{slo.tier ? ` · ${slo.tier}` : ""}{slo.team ? ` · ${slo.team}` : ""}</div>
        </div>
        <span className={`slo-status ${meta.cls}`} title={STATUS_HELP[slo.status]} style={{ cursor: "help" }}>{meta.label}</span>
      </div>

      <div className="slo-objective">
        Target <strong>{slo.objective}%</strong> <span className="slo-window">over {slo.window}</span>
      </div>

      <div className="slo-budget">
        <div className="slo-budget-row">
          <span>Error budget left</span>
          <span className="num">{rem == null ? "—" : `${rem}%`}</span>
        </div>
        <div className="slo-bar" aria-hidden="true">
          <div className={`slo-bar-fill ${meta.cls}`} style={{ width: `${barPct}%` }} />
        </div>
      </div>

      <div className="slo-foot">
        <span
          title={slo.burn_rate_1h == null ? "Burn rate unavailable" : `${slo.burn_rate_1h}× the steady budget-spend rate`}
          style={{ cursor: "help" }}>
          {slo.burn_rate_1h == null ? "burn: n/a" : `burn ${slo.burn_rate_1h}× (1h)`}
        </span>
        {slo.runbook_url && (
          <a href={slo.runbook_url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>runbook</a>
        )}
      </div>
    </div>
  );
}
